import type { FlexProps } from '@chakra-ui/react'
import { Box, Flex, IconButton, Skeleton, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React from 'react'
import { RiArrowLeftLine } from 'react-icons/ri'
import { ROUTE_NAMES, Routes } from 'constants/routes'
import { getTransactions, TransactionListItemTxType as TxType } from 'generated/reactQueryClient'
import { useThemeColors } from 'hooks/useThemeColors'
import { usePreviousRouteContext } from 'providers/PreviousRouteProvider'
import { ARIA_LABEL } from 'utils/aria'
import { captureError } from 'utils/sentry'

type ButtonTitleProps = {
  title: string
  subTitle?: React.ReactNode
} & FlexProps

export const ButtonTitle = ({ title, subTitle, ...props }: ButtonTitleProps) => {
  const COLORS = useThemeColors()
  const router = useRouter()
  const previousRoute = usePreviousRouteContext()

  const handleRedirect = async () => {
    const isDetailPage = [
      `/${ROUTE_NAMES.transaction}/[txHash]`,
      `/${ROUTE_NAMES.blocks}/[id]`,
      `/${ROUTE_NAMES.bundles}/[id]`,
      `/${ROUTE_NAMES.address}/[hash]`,
    ].includes(router.pathname)

    if (!isDetailPage) {
      await router.push(Routes.home())
      return
    }

    if (previousRoute && previousRoute.split('?')[0] !== router.asPath.split('?')[0]) {
      await router.push(previousRoute)
      return
    }

    const detailPage = router.pathname.split('/')[1]

    let tablePage:
      | 'transactions'
      | 'transactionsEnqueued'
      | 'transactionsExit'
      | 'blocks'
      | 'bundles'
      | 'home'

    if (detailPage === ROUTE_NAMES.transaction) {
      const result = await getTransactions({
        tx: router.asPath.split('/')[2],
      })

      switch (result?.data?.[0]?.txType?.toUpperCase()) {
        case TxType.L2:
          tablePage = 'transactions'
          break

        case TxType.L1L2:
          tablePage = 'transactionsEnqueued'
          break

        case TxType.L2L1:
          tablePage = 'transactionsExit'
          break

        default:
          tablePage = 'home'
          break
      }
    } else {
      switch (detailPage) {
        case ROUTE_NAMES.blocks:
          tablePage = 'blocks'
          break

        case ROUTE_NAMES.bundles:
          tablePage = 'bundles'
          break

        default:
          tablePage = 'home'
          break
      }
    }

    await router.push(Routes[tablePage]())
  }

  return router.isReady ? (
    <>
      <Flex w="100%" gap={3} {...props}>
        <IconButton
          onClick={async () => {
            try {
              await handleRedirect()
            } catch (error) {
              if (error instanceof Error) {
                captureError(error)
              }
            }
          }}
          aria-label={ARIA_LABEL.backIcon}
          boxSize="32px"
          fontSize={20}
          bgColor={COLORS.grey07}
          borderRadius={10}
          icon={<RiArrowLeftLine fill={COLORS.dark01} />}
        />
        <Text variant="heading4medium" textTransform="capitalize" whiteSpace="nowrap">
          {title}
        </Text>
        {/* Desktop */}
        <Box display={{ base: 'none', md: 'block' }}>{subTitle}</Box>
      </Flex>
      {/* Mobile */}
      <Box display={{ base: 'block', md: 'none' }}>{subTitle}</Box>
    </>
  ) : (
    <Flex gap={3} {...props}>
      <Skeleton w={8} h={8} borderRadius={10} />
      <Skeleton w={48} h={8} borderRadius={10} />
    </Flex>
  )
}
